import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { XCircle, RefreshCw, Trash2, Loader2, Play, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { adminApi } from '../api/client';

const statusIcons = {
  pending: Clock,
  processing: Loader2,
  completed: CheckCircle,
  failed: XCircle,
  cancelled: AlertCircle,
};

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  processing: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-500/20 text-gray-400',
};

export default function JobsPage() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-jobs', page, statusFilter],
    queryFn: () => adminApi.getJobs({ limit, offset: page * limit, status: statusFilter || undefined }),
    refetchInterval: 5000,
  });

  const cancelJobMutation = useMutation({
    mutationFn: (id: string) => adminApi.cancelJob(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-jobs'] }),
  });

  const deleteJobMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteJob(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-jobs'] }),
  });

  const retryJobMutation = useMutation({
    mutationFn: (id: string) => adminApi.retryJob(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-jobs'] }),
  });

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">任務管理</h1>
        <p className="text-gray-400 mt-1">查看與管理視頻生成任務</p>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(0);
          }}
          className="input w-48"
        >
          <option value="">全部狀態</option>
          <option value="pending">待處理</option>
          <option value="processing">處理中</option>
          <option value="completed">已完成</option>
          <option value="failed">失敗</option>
          <option value="cancelled">已取消</option>
        </select>
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="table-header">任務</th>
              <th className="table-header">用戶</th>
              <th className="table-header">提供商</th>
              <th className="table-header">狀態</th>
              <th className="table-header">進度</th>
              <th className="table-header">創建時間</th>
              <th className="table-header">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-500" />
                </td>
              </tr>
            ) : (data?.jobs || []).map((job) => {
              const StatusIcon = statusIcons[job.status];
              return (
                <tr key={job.id} className="hover:bg-gray-700/30">
                  <td className="table-cell">
                    <div className="max-w-xs">
                      <p className="text-sm text-white truncate">{job.prompt}</p>
                      <p className="text-xs text-gray-500 font-mono">{job.id.slice(0, 8)}...</p>
                    </div>
                  </td>
                  <td className="table-cell">
                    <p className="text-sm">{job.user?.name || job.userId.slice(0, 8)}</p>
                  </td>
                  <td className="table-cell">
                    <span className="px-2 py-1 bg-gray-700 rounded text-xs uppercase">
                      {job.provider}
                    </span>
                  </td>
                  <td className="table-cell">
                    <span className={clsx('px-2 py-1 text-xs rounded-full flex items-center space-x-1 w-fit', statusColors[job.status])}>
                      <StatusIcon className={clsx('w-3 h-3', job.status === 'processing' && 'animate-spin')} />
                      <span>{job.status}</span>
                    </span>
                  </td>
                  <td className="table-cell">
                    {job.status === 'processing' ? (
                      <div className="w-20">
                        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary-500 transition-all"
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{job.progress}%</p>
                      </div>
                    ) : (
                      <span className="text-gray-500">-</span>
                    )}
                  </td>
                  <td className="table-cell">
                    {new Date(job.createdAt).toLocaleString('zh-TW')}
                  </td>
                  <td className="table-cell">
                    <div className="flex space-x-1">
                      {job.status === 'completed' && job.videoUrl && (
                        <a
                          href={job.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-green-400 hover:bg-green-500/20 rounded-lg"
                        >
                          <Play className="w-4 h-4" />
                        </a>
                      )}
                      {job.status === 'failed' && (
                        <button
                          onClick={() => retryJobMutation.mutate(job.id)}
                          className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-500/20 rounded-lg"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      {(job.status === 'pending' || job.status === 'processing') && (
                        <button
                          onClick={() => cancelJobMutation.mutate(job.id)}
                          className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/20 rounded-lg"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (confirm('確定要刪除此任務？')) {
                            deleteJobMutation.mutate(job.id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            共 {data?.total} 個任務
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="px-4 py-2 text-gray-400">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-secondary disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
